
    import Component from "vue-class-component";
    import Vue from "vue";
    import {
        AvailableActions, EducationalInstitutionModel,
        QualificationRecordListItem, QualificationRecordsClient,
        QualificationRecordsSortField,
        SpecialityType, SysAdminOrganizationsClient,
    } from "@/api-client";
    import {BvTableCtxObject, BvTableFieldArray} from "bootstrap-vue";
    import TableEmptySlot from "@/features/shared/components/tables/TableEmptySlot.vue";
    import Loader from "@/features/shared/components/Loader";
    import HeadingWave from "@/features/shared/components/HeadingWave.vue";
    import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
    import {debounceFn} from "debounce-decorator-ts";
    import ServersidePaging from "@/components/ServersidePaging";
    import _ from "underscore";
    import CardQuicklink from "@/features/dashboard/components/CardQuicklink.vue";
    import {Route} from "vue-router";
    import {Prop} from "vue-property-decorator";
    import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";
    import {NavigationGuardNext} from "vue-router/types/router";
    import Organizations from "@/features/administration/organizations/Organizations.vue";
    import ServersidePagingSelector from "@/components/ServersidePagingSelector.vue";
    import { formatDate } from "@/formatting/dateFormatter";

    const specialityString = "name";
    const issuedOnString = "issuedOn";
    const educationalInstitutionNameString = "educationalInstitutionName";
    const submittedOnString = "submittedOn";
    const defaultSortValue = QualificationRecordsSortField.IssuedOnDesc;

    class Filters {
        constructor(
            public institutions:EducationalInstitutionModel[],
            public issuedOnOrAfter: Date | null,
            public issuedOnOrBefore: Date | null,
            public searchPhrase?: string) {}

        public removeInstitution(institution: EducationalInstitutionModel) {
            this.institutions = _.without(this.institutions, institution);
        }

        public resetAll() {
          this.institutions = [];
          this.issuedOnOrAfter = null;
          this.issuedOnOrBefore = null;
          this.searchPhrase = undefined;
        }

        public noneApplied(): boolean {
          return this.institutions.length == 0
              && this.issuedOnOrAfter == null
              && this.issuedOnOrBefore == null
              && this.searchPhrase == undefined || this.searchPhrase?.trim().length == 0;
        }
    }

    class Sort {
        value: QualificationRecordsSortField | null = null;

        constructor(value: QualificationRecordsSortField | null = defaultSortValue) {
            this.value = value;
        }

        get fieldName(): string | null {
            switch (this.value) {
                case QualificationRecordsSortField.SpecialityNameAsc:
                case QualificationRecordsSortField.SpecialityNameDesc:
                    return specialityString;
                case QualificationRecordsSortField.IssuedOnAsc:
                case QualificationRecordsSortField.IssuedOnDesc:
                    return issuedOnString;
                case QualificationRecordsSortField.EducationalInstitutionNameAsc:
                case QualificationRecordsSortField.EducationalInstitutionNameDesc:
                    return educationalInstitutionNameString;
                case QualificationRecordsSortField.SubmittedOnAsc:
                case QualificationRecordsSortField.SubmittedOnDesc:
                    return submittedOnString;
                default:
                    return null;
            }
        }

        get isSortedByDesc(): boolean {
            return this.value == null
            || this.value == QualificationRecordsSortField.SpecialityNameDesc
            || this.value == QualificationRecordsSortField.IssuedOnDesc
            || this.value == QualificationRecordsSortField.EducationalInstitutionNameDesc
            || this.value == QualificationRecordsSortField.SubmittedOnDesc;
        }
    }

    interface Props {
        availableActions: AvailableActions;
    }

    @Component({
        components:{
            TableEmptySlot,
            HeadingWave,
            SectionPageHeading,
            CardQuicklink,
            HeadingGroup,
            ServersidePagingSelector
        },
        beforeRouteEnter(to, from, next: NavigationGuardNext<QualificationRecords>) {
            const props = to.meta!.props as Props;
            Promise.all([
                new QualificationRecordsClient().availableActions()
            ]).then(values => {
                props.availableActions = values[0];
                next();
            }).catch(e => next(e))
        }
    })
    export default class QualificationRecords extends Vue implements Props {
        @Prop({ required: true })
        availableActions!: AvailableActions;

        records: QualificationRecordListItem[] = [];
        institutions: EducationalInstitutionModel[] = [];

        loader = new Loader(true);
        filters = new Filters([], null, null);
        paging = new ServersidePaging();
        sort = new Sort();

        async created(){
            if (this.availableActions.canViewAllQualificationRecords)
              this.institutions = await new QualificationRecordsClient().educationalInstitutions();

            this.getParametersFromRouteOrDefault();

            this.$watch(() => this.filters, () => {
                this.paging.pageNumber = 1;
                this.reflectParametersToRoute();
            }, { deep: true });

            this.$watch(() => this.paging.pageNumber, () => {
                this.reflectParametersToRoute();
            });

            this.$watch(() => this.paging.pageSize, () => {
                this.paging.pageNumber = 1;
                this.reflectParametersToRoute();
            });

            this.$watch(() => this.sort, () => {
                this.reflectParametersToRoute();
            }, { deep: true });

            this.$watch(() => this.$route.query, () => {
              this.search();
            }, { deep: true, immediate: true });
        }

        navigateToDetails(record: QualificationRecordListItem){
            const routeMap: { [id: string] : string; } = {};
            routeMap[SpecialityType.Educational] = "educationalRecordDetails";
            routeMap[SpecialityType.Training] = "trainingRecordDetails";
            this.$router.push({name: routeMap[record.specialityType], params: {id: record.id}});
        }

        getSubmittedHtml(qualification: QualificationRecordListItem){
            return !qualification.submittedOn
                ? `<span class="badge badge-secondary">${this.$ls.Documents_Qualifications_Records_RecordState_NotSubmitted}</span>`
                : `<span class="badge badge-success">${this.$ls.Documents_Qualifications_Records_RecordState_Submitted}</span>`;
        }

        search(){
            this.records = [];

            new QualificationRecordsClient()
                .setLoader(this.loader)
                .search(
                    this.filters.issuedOnOrAfter,
                    this.filters.issuedOnOrBefore,
                    this.filters.institutions.map(x => x.id),
                    this.sort.value,
                    this.paging.pageNumber,
                    this.paging.pageSize,
                    this.filters.searchPhrase ? this.filters.searchPhrase : "")
                .then(
                    queryResult => {
                        this.records = queryResult.pageResult;
                        this.paging.totalCount = queryResult.totalCount;
                        this.paging.pageSize = queryResult.pageSize;
                    });
        }

        @debounceFn(100) // Prevents possible double-update
        reflectParametersToRoute(): void {
            this.$router.replace({
                query: {
                    // Setting to 'undefined' removes parameter from URL.
                    searchPhrase: this.filters.searchPhrase || undefined,
                    pageNumber: this.paging.pageNumber ? this.paging.pageNumber.toString() : undefined,
                    pageSize: this.paging.pageSize ? this.paging.pageSize.toString() : undefined,
                    issuedOnOrAfterDate: this.filters.issuedOnOrAfter ? this.filters.issuedOnOrAfter.getDate().toString() : undefined,
                    issuedOnOrAfterMonth: this.filters.issuedOnOrAfter ? this.filters.issuedOnOrAfter.getMonth().toString() : undefined,
                    issuedOnOrAfterYear: this.filters.issuedOnOrAfter ? this.filters.issuedOnOrAfter.getFullYear().toString() : undefined,
                    issuedOnOrBeforeDate: this.filters.issuedOnOrBefore ? this.filters.issuedOnOrBefore.getDate().toString() : undefined,
                    issuedOnOrBeforeMonth: this.filters.issuedOnOrBefore ? this.filters.issuedOnOrBefore.getMonth().toString() : undefined,
                    issuedOnOrBeforeYear: this.filters.issuedOnOrBefore ? this.filters.issuedOnOrBefore.getFullYear().toString() : undefined,
                    institutions: this.filters.institutions.length > 0 ? this.filters.institutions.map(x => x.id).join(',') : undefined,
                    sort: this.sort.value || undefined,
                }
            });
        }

        getParametersFromRouteOrDefault() {
            const route = this.$route as any;
            const query = route.query;
            const searchPhrase : string | undefined = query.searchPhrase;
            const issuedOnOrAfter : Date | null = query.issuedOnOrAfterDate ? new Date(query.issuedOnOrAfterYear, query.issuedOnOrAfterMonth, query.issuedOnOrAfterDate) : null;
            const issuedOnOrBefore : Date | null = query.issuedOnOrBeforeDate ? new Date(query.issuedOnOrBeforeYear, query.issuedOnOrBeforeMonth, query.issuedOnOrBeforeDate) : null;
            const rawInstitutions: string[] = query.institutions ? query.institutions.split(',') : [];
            const institutions : EducationalInstitutionModel[] = this.institutions.filter(x => rawInstitutions.some(r => r == x.id));
            this.filters = new Filters(institutions, issuedOnOrAfter, issuedOnOrBefore, searchPhrase);
            this.paging.pageNumber = parseInt(route.query.pageNumber) || this.paging.pageNumber;
            this.paging.pageSize = parseInt(route.query.pageSize) || this.paging.pageSize;
            this.sort = new Sort(route.query.sort || defaultSortValue);
        }

        updateSort(ctx: BvTableCtxObject) {
            switch (ctx.sortBy) {
                case specialityString:
                    this.sort = new Sort(ctx.sortDesc ? QualificationRecordsSortField.SpecialityNameDesc : QualificationRecordsSortField.SpecialityNameAsc)
                    break;
                case issuedOnString:
                    this.sort = new Sort(ctx.sortDesc ? QualificationRecordsSortField.IssuedOnDesc : QualificationRecordsSortField.IssuedOnAsc)
                    break;
                case educationalInstitutionNameString:
                    this.sort = new Sort(ctx.sortDesc ? QualificationRecordsSortField.EducationalInstitutionNameDesc : QualificationRecordsSortField.EducationalInstitutionNameAsc)
                    break;
                case submittedOnString:
                    this.sort = new Sort(ctx.sortDesc ? QualificationRecordsSortField.SubmittedOnDesc : QualificationRecordsSortField.SubmittedOnAsc)
                    break;
                default:
                    this.sort = new Sort();
            }
        }

        officialField = {
          label: this.$ls.Documents_Qualifications_Records_Table_Label_EducationalInstitution
        };

        nonOfficialField = {
          label: this.$ls.Documents_Qualifications_Records_Table_Label_SpecialityType,
          formatter: (value: any, key: any, item: QualificationRecordListItem) => {
            return item.specialityType == SpecialityType.Training
				? this.$ls.Documents_Qualifications_SpecialityType_TrainingCourses
				: this.$ls.Documents_Qualifications_SpecialityType_FormalEducation;
          }
        };

        fields:BvTableFieldArray = [
            {
                key: 'recordNumber',
                label: this.$ls.Documents_Qualifications_Label_RecordNumber,
                sortable: false,
            },
            {
                key: specialityString,
                label: this.$ls.Global_Label_Speciality,
                sortable: true,
                thClass: 'w-30',
                formatter: (value: any, key: any, item: QualificationRecordListItem) => item.name
            },
            {
                key: issuedOnString,
                label: this.$ls.Global_Label_IssueDateShort,
                sortable: true,
                formatter: (value: any, key: any, item: QualificationRecordListItem) => formatDate(item.issuedOn, "-")
            },
            {
                key: educationalInstitutionNameString,
                ...(this.availableActions.canViewAllQualificationRecords ? this.officialField : this.nonOfficialField),
                thClass: 'w-20',
                sortable: true,
            },
            {
                key: submittedOnString,
                label: this.$ls.Documents_Qualifications_Records_Table_Label_RecordState,
                sortable: true,
                formatter: (value: any, key: any, item: QualificationRecordListItem) => item.submittedOn,
            },
            {
                key: 'linkIcon',
                label: '',
                sortable: false,
                tdClass: 'table-actions'
            }
        ]
    }
